import React, { useEffect, useState, Children, lazy } from 'react';
import { Check, X, ChevronRight, MessageCircle, AlertTriangle, DollarSign, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatBubble } from './ChatBubble';
import { AudioPreview } from './AudioPreview';
import { SocialProofToast } from './SocialProofToast';
import { LiveViewerFloat } from './LiveViewerFloat';
function LiveViewerCount() {
  const [count, setCount] = useState(Math.floor(Math.random() * 15) + 12);
  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => {
        const change = Math.floor(Math.random() * 5) - 2; // -2 to +2
        const next = prev + change;
        return Math.max(8, Math.min(35, next));
      });
    }, 4000 + Math.random() * 3000); // every 4-7 seconds
    return () => clearInterval(interval);
  }, []);
  return <div className="flex items-center justify-center gap-2 py-2 bg-[#0a0a0a]" data-id="element-40">
      <span className="relative flex h-2 w-2" data-id="element-41">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-42" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" data-id="element-43" />
      </span>
      <span className="text-xs text-gray-400" data-id="element-44">
        <span className="text-white font-bold" data-id="element-45">{count}</span> personas viendo
        esta página ahora
      </span>
    </div>;
}
export function SalesPage() {
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 900);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-[#0a0a0a] text-gray-100 font-sans selection:bg-[#25D366] selection:text-black overflow-x-hidden pt-10" data-id="element-46">
      {/* URGENCY BAR */}
      <div className="fixed top-0 left-0 w-full bg-[#25D366] text-black z-50 py-1.5 px-4 text-center font-bold text-sm md:text-base shadow-lg flex items-center justify-center gap-2" data-id="element-47">
        <AlertTriangle className="w-4 h-4" data-id="element-48" />
        <span data-id="element-49">Oferta Flash: El precio sube a $15 en</span>
        <span className="bg-black text-[#25D366] px-2 py-0.5 rounded font-mono min-w-[60px]" data-id="element-50">
          {formatTime(timeLeft)}
        </span>
      </div>

      {/* Live Viewer Count */}
      <LiveViewerCount data-id="element-51" />

      {/* 1. HERO SECTION */}
      <section className="relative pt-4 pb-8 px-4 md:pt-10 md:pb-12 overflow-hidden" data-id="element-52">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-[#1a2e24] via-[#0a0a0a] to-[#0a0a0a] opacity-60 -z-10" data-id="element-53" />

        <div className="max-w-md mx-auto md:max-w-4xl flex flex-col md:flex-row items-center gap-12" data-id="element-54">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="flex-1 text-center md:text-left" data-id="element-55">
            <div className="inline-block px-3 py-1 mb-6 border border-[#25D366]/30 rounded-full bg-[#25D366]/10 text-[#25D366] text-xs font-bold tracking-wider uppercase" data-id="element-56">
              Solo $5 USD • Oferta Limitada
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6 text-white tracking-tight" data-id="element-57">
              Si hoy estás{' '}
              <span className="text-[#25D366] underline decoration-4 underline-offset-4" data-id="element-58">
                pelando
              </span>
              : haz tu primera venta en menos de 7 días.
            </h1>

            <p className="text-lg md:text-xl text-gray-400 mb-8 leading-relaxed" data-id="element-59">
              20 micro-servicios (físicos + digitales) + mensajes copy/paste +
              cobro simple. <br className="hidden md:block" data-id="element-60" />
              <span className="text-white font-semibold" data-id="element-61">
                Sin seguidores. Sin invertir. Sin humo.
              </span>
            </p>

            <p className="mt-4 text-xs text-gray-500" data-id="element-62">
              *Pago único de $5. Entrega inmediata por WhatsApp.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20,
          rotate: 5
        }} animate={{
          opacity: 1,
          x: 0,
          rotate: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="flex-1 relative w-full max-w-[320px] md:max-w-sm" data-id="element-63">
            <div className="absolute inset-0 bg-[#25D366] blur-[60px] opacity-20 rounded-full" data-id="element-64" />
            <img src="https://cdn.magicpatterns.com/uploads/c6Cgx1gXxRmxEUJE98DyJN/openrouter-generated-image-1770537137010.jpg" alt="Mano sosteniendo celular con mensaje de venta" loading="lazy" className="relative z-10 w-full rounded-3xl border-4 border-[#202c33] shadow-2xl rotate-[-2deg] hover:rotate-0 transition-transform duration-500" data-id="element-65" />
          </motion.div>
        </div>
      </section>

      {/* AUDIO PREVIEW SECTION */}
      <section className="pt-0 pb-12 px-4 bg-[#0a0a0a] border-t-0 border-white/5" data-id="element-66">
        <div className="max-w-lg mx-auto flex flex-col items-center" data-id="element-67">
          <AudioPreview data-id="element-68" />

          <motion.a href="#precio" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} animate={{
          boxShadow: ['0 0 0 rgba(37, 211, 102, 0)', '0 0 20px rgba(37, 211, 102, 0.5)', '0 0 0 rgba(37, 211, 102, 0)']
        }} transition={{
          boxShadow: {
            duration: 2,
            repeat: Infinity
          }
        }} className="relative overflow-hidden inline-flex items-center justify-center w-full md:w-auto gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-black font-black text-lg px-8 py-4 rounded-xl shadow-[0_0_20px_rgba(37,211,102,0.3)] transition-all group" data-id="element-69">
            {/* Shimmer effect */}
            <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10" data-id="element-70" />

            <MessageCircle className="w-6 h-6 fill-current relative z-20" data-id="element-71" />
            <span className="relative z-20" data-id="element-72">SÍ, QUIERO VENDER YA</span>
            <ChevronRight className="w-5 h-5 relative z-20" data-id="element-73" />
          </motion.a>

          <p className="mt-4 text-xs text-gray-500" data-id="element-74">
            *Pago único de $5. Entrega inmediata por WhatsApp.
          </p>
        </div>
      </section>

      {/* 2. PAIN AGITATION SECTION */}
      <section className="py-16 px-4 bg-[#111b21] border-t border-white/5" data-id="element-75">
        <div className="max-w-2xl mx-auto" data-id="element-76">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} data-id="element-77">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 text-white" data-id="element-78">
              El problema real
            </h2>
            <p className="text-center text-gray-500 mb-10 font-medium" data-id="element-79">
              (y nadie te lo dice)
            </p>

            <div className="bg-[#1f2c34] p-6 md:p-8 rounded-2xl border border-white/5 shadow-lg" data-id="element-80">
              <p className="text-lg text-gray-300 mb-6" data-id="element-81">
                No es que no haya oportunidades. Es que cuando tienes urgencia,
                te pasa esto:
              </p>

              <ul className="space-y-4" data-id="element-82">
                {["Buscas 'cómo ganar dinero' y te salen 50 ideas", 'No eliges ninguna por parálisis', 'Te da pena escribir y ofrecer', 'Cuando por fin escribes, no sabes cerrar', 'Te dejan en visto y no sabes cobrar'].map((item, i) => <motion.li key={i} variants={itemVariants} className="flex items-start gap-3" data-id="element-83">
                    <div className="mt-1 min-w-[24px] h-6 flex items-center justify-center rounded-full bg-red-500/10 text-red-500" data-id="element-84">
                      <X className="w-4 h-4" data-id="element-85" />
                    </div>
                    <span className="text-gray-300" data-id="element-86">{item}</span>
                  </motion.li>)}
              </ul>

              <div className="mt-8 pt-6 border-t border-white/10" data-id="element-87">
                <p className="text-xl font-bold text-center text-white" data-id="element-88">
                  Resultado:{' '}
                  <span className="text-red-400" data-id="element-89">
                    otra semana sin una sola venta.
                  </span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3. MECHANISM SECTION */}
      <section className="py-16 px-4 bg-[#0a0a0a]" data-id="element-90">
        <div className="max-w-3xl mx-auto text-center" data-id="element-91">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-92">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-93">
              Lo que este PDF hace diferente
            </h2>
            <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto" data-id="element-94">
              Esto no es un curso. No es teoría. No es "mentalidad". <br data-id="element-95" />
              Es un sistema corto para que hagas una cosa: <br data-id="element-96" />
              <span className="text-[#25D366] font-bold text-2xl" data-id="element-97">
                tu primera venta digital por WhatsApp.
              </span>
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-98">
              {[{
              icon: MessageCircle,
              text: 'Mensaje Corto'
            }, {
              icon: ChevronRight,
              text: 'Paso Claro'
            }, {
              icon: Check,
              text: 'Cierre Fácil'
            }, {
              icon: DollarSign,
              text: 'Cobro Simple'
            }].map((item, i) => <motion.div key={i} whileHover={{
              y: -5
            }} className="bg-[#111b21] p-6 rounded-xl border border-[#25D366]/20 flex flex-col items-center gap-3" data-id="element-99">
                  <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center text-[#25D366]" data-id="element-100">
                    <item.icon className="w-6 h-6" data-id="element-101" />
                  </div>
                  <span className="font-bold text-sm md:text-base" data-id="element-102">
                    {item.text}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-16 px-4 bg-[#0a0a0a] border-t border-white/5 overflow-hidden" data-id="element-103">
        <div className="max-w-6xl mx-auto" data-id="element-104">
          <div className="text-center mb-12" data-id="element-105">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-106">
              Lo que dice la gente{' '}
              <span className="text-[#25D366]" data-id="element-107">que ya aplicó</span>
            </h2>
            <p className="text-gray-400" data-id="element-108">
              Capturas reales de gente que dejó el miedo y empezó a cobrar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-109">
            {[{
            name: 'Ana Sofía',
            msg: 'Chama, no sabía cobrar y con el script de USDT me pagaron de una sin tanta vuelta. Gracias!',
            date: 'Ayer',
            time: '9:15 a.m.',
            reply: '¡Qué bueno Ana! El script de USDT está diseñado para que el proceso sea directo y sin complicaciones. Me alegra que te haya funcionado 💪',
            replyTime: '9:22 a.m.',
            img: 'https://cdn.magicpatterns.com/uploads/2JxnDGrjds6TfggamXRmet/aaa1ee39d5438ac27ee0bee1706a75cd.jpg'
          }, {
            name: 'Carlos R.',
            msg: "Mano, qué locura este método. Ya saqué pa' los frescos en un día. Brutal.",
            date: 'Hoy',
            time: '10:42 a.m.',
            reply: '¡Excelente Carlos! Eso es lo que buscamos, resultados rápidos con acción directa. Sigue aplicando el método 🔥',
            replyTime: '10:50 a.m.',
            img: 'https://cdn.magicpatterns.com/uploads/dbKj8usGk6AYDKYa2ZVJfV/99f5492f9690a165ca672041af39b43c.jpg'
          }, {
            name: 'Mariangel',
            msg: 'Lo del pago móvil me salvó. La gente paga más rápido así cuando les das el texto listo.',
            date: '31/01/2026',
            time: '11:20 a.m.',
            reply: 'Sí, el pago móvil ayuda mucho con eso, me alegra que te haya funcionado!',
            replyTime: '1:27 p.m.',
            img: 'https://cdn.magicpatterns.com/uploads/mZaBC4uiP1hgxvLYgKtJeV/5a65e3715a52e21c309005371ccc5167.jpg'
          }, {
            name: 'Luis D.',
            msg: 'Mi pana, esto es otro nivel. 100% real no fake, ya cuadré 2 ventas hoy.',
            date: 'Ayer',
            time: '2:15 p.m.',
            reply: '¡Brutal Luis! 2 ventas en un día es un gran comienzo. Mantén ese ritmo y vas a ver resultados increíbles 📈',
            replyTime: '2:25 p.m.',
            img: 'https://cdn.magicpatterns.com/uploads/cByuS8CN9Z753W1CLeRkiw/2afe28b53751457bb2c7f2d3df77b714.jpg'
          }, {
            name: 'Andrea P.',
            msg: 'Mi primera venta fue hoy! Hice lo de la manicura express y ya tengo agenda llena pal finde.',
            date: 'Hoy',
            time: '2:30 p.m.',
            reply: '¡Felicidades Andrea! La manicura express es uno de los servicios más rentables. Agenda llena = misión cumplida ✨',
            replyTime: '2:38 p.m.',
            img: 'https://cdn.magicpatterns.com/uploads/87f2JbWJjJz9RxcTsvxKnS/63ab2e90fbb5f429f1345097c3f8d527.jpg'
          }, {
            name: 'Roberto',
            msg: 'Brutal mi rey. Ya tengo 3 clientes fijos con lo de los perros. Fino todo.',
            date: '30/01/2026',
            time: '4:20 p.m.',
            reply: '¡Genial Roberto! Clientes fijos es la clave del negocio sostenible. Sigue dando un servicio de calidad 🐕',
            replyTime: '4:35 p.m.',
            img: 'https://cdn.magicpatterns.com/uploads/3GCWPs7obpiFW48pnpLBX4/bd7ee7288b247e4242427e67661cad03.jpg'
          }, {
            name: 'Valeria',
            msg: 'Ya no me da pena cobrar. El guión es súper directo y la gente lo respeta más.',
            date: 'Ayer',
            time: '7:20 p.m.',
            reply: 'Así es Valeria, cuando cobras con seguridad la gente te toma más en serio. Esa confianza se nota 💯',
            replyTime: '7:30 p.m.',
            img: 'https://cdn.magicpatterns.com/uploads/ssqwqG9JqkyKcaZNpJMp1H/eca99e18d08e364c2be0748dd97b8dc0.jpg'
          }, {
            name: 'Miguel A.',
            msg: 'Lacra total el script de cierre. Nadie me deja en visto ahora. Activo.',
            date: 'Hoy',
            time: '1:10 p.m.',
            reply: '¡Eso es Miguel! El script de cierre elimina la fricción. Cuando das el siguiente paso claro, la gente responde 🎯',
            replyTime: '1:18 p.m.',
            img: 'https://cdn.magicpatterns.com/uploads/rYWCVtfVfvsRQm46HofCcC/5639ecc90b2b9ef0f8ee308b63d45001.jpg'
          }, {
            name: 'Patricia',
            msg: 'Gracias pana, por fin dejé de dar vueltas y empecé a facturar. Lo necesitaba full.',
            date: 'Hoy',
            time: '10:05 a.m.',
            reply: '¡Me alegra mucho Patricia! Dejar de dar vueltas y pasar a la acción es el paso más importante. Sigue así 🚀',
            replyTime: '10:15 a.m.',
            img: 'https://cdn.magicpatterns.com/uploads/jB1uMJcAMFXzeDt3E4NGiP/b748d0877baa7fe191184679a9c58661.jpg'
          }].map((testi, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.06
          }} className="max-w-[340px] mx-auto w-full" data-id="element-110">
                {/* Phone Shell */}
                <div className="rounded-[2rem] border-[8px] border-[#c8c8c8] bg-[#c8c8c8] shadow-[0_10px_50px_rgba(0,0,0,0.6)] overflow-hidden" data-id="element-111">
                  {/* Inner screen */}
                  <div className="rounded-[1.4rem] overflow-hidden bg-black flex flex-col" data-id="element-112">
                    {/* Notch / Speaker */}
                    <div className="bg-black h-5 flex items-center justify-center" data-id="element-113">
                      <div className="w-14 h-[3px] bg-[#444] rounded-full" data-id="element-114" />
                    </div>

                    {/* Status bar */}
                    <div className="bg-[#075E54] flex items-center justify-between px-4 py-0.5 text-white text-[10px] font-medium" data-id="element-115">
                      <span data-id="element-116">9:41</span>
                      <div className="flex items-center gap-1" data-id="element-117">
                        {/* Signal bars */}
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="white" className="opacity-80" data-id="element-118">
                          <rect x="0" y="7" width="2" height="3" rx="0.5" data-id="element-119" />
                          <rect x="3" y="5" width="2" height="5" rx="0.5" data-id="element-120" />
                          <rect x="6" y="3" width="2" height="7" rx="0.5" data-id="element-121" />
                          <rect x="9" y="0" width="2" height="10" rx="0.5" data-id="element-122" />
                        </svg>
                        {/* Battery */}
                        <svg width="16" height="9" viewBox="0 0 16 9" fill="none" className="opacity-80" data-id="element-123">
                          <rect x="0.5" y="0.5" width="13" height="8" rx="1.5" stroke="white" data-id="element-124" />
                          <rect x="1.5" y="1.5" width="11" height="6" rx="0.5" fill="white" data-id="element-125" />
                          <rect x="14" y="2.5" width="2" height="4" rx="0.5" fill="white" data-id="element-126" />
                        </svg>
                      </div>
                    </div>

                    {/* WhatsApp Header */}
                    <div className="bg-[#075E54] px-1.5 pb-3 pt-1 flex items-center gap-1" data-id="element-127">
                      {/* Back arrow */}
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="white" className="shrink-0 opacity-90" data-id="element-128">
                        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" transform="scale(-1,1) translate(-24,0)" data-id="element-129" />
                      </svg>
                      {/* Avatar */}
                      <div className="w-9 h-9 rounded-full overflow-hidden border border-white/20 shrink-0" data-id="element-130">
                        <img src={testi.img} alt={testi.name} loading="lazy" className="w-full h-full object-cover" data-id="element-131" />
                      </div>
                      {/* Name + status */}
                      <div className="flex-1 min-w-0 ml-0.5" data-id="element-132">
                        <p className="text-white text-[15px] font-semibold leading-tight truncate" data-id="element-133">
                          {testi.name}
                        </p>
                        <p className="text-[#a8d8c8] text-[11px] leading-tight" data-id="element-134">
                          en linea
                        </p>
                      </div>
                      {/* Action icons */}
                      <div className="flex items-center gap-3 text-white pr-0.5 shrink-0" data-id="element-135">
                        {/* Phone */}
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="opacity-90" data-id="element-136">
                          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" data-id="element-137" />
                        </svg>
                        {/* 3 dots */}
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="opacity-90" data-id="element-138">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" data-id="element-139" />
                        </svg>
                      </div>
                    </div>

                    {/* Chat Area */}
                    <div className="flex-1 flex flex-col gap-2 p-3 relative" style={{
                  backgroundColor: '#e4ddd4',
                  backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
                  backgroundSize: '300px',
                  backgroundBlendMode: 'soft-light'
                }} data-id="element-140">
                      {/* Date pill */}
                      <div className="flex justify-center my-1" data-id="element-141">
                        <span className="bg-[#d9f0e6] text-[#4a7c6a] text-[11px] px-3 py-1 rounded-md shadow-sm font-medium" data-id="element-142">
                          {testi.date}
                        </span>
                      </div>

                      {/* Incoming bubble (customer) */}
                      <div className="self-start max-w-[85%]" data-id="element-143">
                        <div className="bg-white rounded-lg rounded-tl-none shadow-[0_1px_1px_rgba(0,0,0,0.13)] px-2.5 pt-1.5 pb-1 relative" data-id="element-144">
                          {/* Tail */}
                          <div className="absolute -left-2 top-0 w-0 h-0" style={{
                        borderTop: '8px solid white',
                        borderLeft: '8px solid transparent'
                      }} data-id="element-145" />
                          <p className="text-[#303030] text-[14px] leading-[19px] pr-1" data-id="element-146">
                            {testi.msg}
                          </p>
                          <div className="flex items-center justify-end gap-1 -mt-0.5 pb-0.5" data-id="element-147">
                            <span className="text-[10px] text-[#8c9a9e]" data-id="element-148">
                              {testi.time}
                            </span>
                            <svg viewBox="0 0 16 11" width="16" height="11" fill="#53bdeb" data-id="element-149">
                              <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.44-2.862-2.799a.405.405 0 0 0-.305-.133.474.474 0 0 0-.336.156.57.57 0 0 0-.14.373c0 .14.05.26.152.357l3.17 3.096a.46.46 0 0 0 .312.132.485.485 0 0 0 .392-.184L11.17 1.07a.49.49 0 0 0 .102-.38.363.363 0 0 0-.201-.038zm-3.367 8.06l.46-.557-3.17-3.096-.46.557 3.17 3.096zM15.229.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.44-1.088-1.064-.46.557 1.396 1.365a.46.46 0 0 0 .312.132.485.485 0 0 0 .392-.184L15.328 1.07a.49.49 0 0 0 .102-.38.363.363 0 0 0-.201-.038z" data-id="element-150" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Outgoing bubble (reply) */}
                      <div className="self-end max-w-[85%]" data-id="element-151">
                        <div className="rounded-lg rounded-tr-none shadow-[0_1px_1px_rgba(0,0,0,0.13)] px-2.5 pt-1.5 pb-1 relative" style={{
                      backgroundColor: '#d9fdd3'
                    }} data-id="element-152">
                          {/* Tail */}
                          <div className="absolute -right-2 top-0 w-0 h-0" style={{
                        borderTop: '8px solid #d9fdd3',
                        borderRight: '8px solid transparent'
                      }} data-id="element-153" />
                          <p className="text-[#303030] text-[14px] leading-[19px] pr-1" data-id="element-154">
                            {testi.reply}
                          </p>
                          <div className="flex items-center justify-end gap-1 -mt-0.5 pb-0.5" data-id="element-155">
                            <span className="text-[10px] text-[#8c9a9e]" data-id="element-156">
                              {testi.replyTime}
                            </span>
                            <svg viewBox="0 0 16 11" width="16" height="11" fill="#53bdeb" data-id="element-157">
                              <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.44-2.862-2.799a.405.405 0 0 0-.305-.133.474.474 0 0 0-.336.156.57.57 0 0 0-.14.373c0 .14.05.26.152.357l3.17 3.096a.46.46 0 0 0 .312.132.485.485 0 0 0 .392-.184L11.17 1.07a.49.49 0 0 0 .102-.38.363.363 0 0 0-.201-.038zm-3.367 8.06l.46-.557-3.17-3.096-.46.557 3.17 3.096zM15.229.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.44-1.088-1.064-.46.557 1.396 1.365a.46.46 0 0 0 .312.132.485.485 0 0 0 .392-.184L15.328 1.07a.49.49 0 0 0 .102-.38.363.363 0 0 0-.201-.038z" data-id="element-158" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Input Bar */}
                    <div className="bg-[#f0f0f0] px-2 py-1.5 flex items-center gap-1.5" data-id="element-159">
                      <div className="flex-1 bg-white rounded-full h-9 flex items-center px-3 gap-2" data-id="element-160">
                        {/* Emoji */}
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="#919191" className="shrink-0" data-id="element-161">
                          <path d="M9.153 11.603c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zm5.694 0c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zM12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm0-3.5c-2.33 0-4.31-1.46-5.11-3.5h10.22c-.8 2.04-2.78 3.5-5.11 3.5z" data-id="element-162" />
                        </svg>
                        <span className="text-[#919191] text-[14px] flex-1" data-id="element-163">
                          Escribe un mensaje...
                        </span>
                        {/* Clip */}
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="#919191" className="shrink-0 rotate-[-45deg]" data-id="element-164">
                          <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" data-id="element-165" />
                        </svg>
                        {/* Camera */}
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="#919191" className="shrink-0" data-id="element-166">
                          <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" data-id="element-167" />
                          <circle cx="12" cy="12" r="3" data-id="element-168" />
                        </svg>
                      </div>
                      {/* Mic button */}
                      <div className="w-9 h-9 rounded-full bg-[#00a884] flex items-center justify-center shrink-0" data-id="element-169">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="white" data-id="element-170">
                          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" data-id="element-171" />
                          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" data-id="element-172" />
                        </svg>
                      </div>
                    </div>

                    {/* Phone chin with home indicator */}
                    <div className="bg-black h-4 flex items-center justify-center" data-id="element-173">
                      <div className="w-20 h-1 bg-[#444] rounded-full" data-id="element-174" />
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 4. SERVICES SECTION */}
      <section className="py-16 px-4 bg-[#111b21] relative overflow-hidden" data-id="element-175">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(#25D366 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-176"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-177">
          <div className="text-center mb-12" data-id="element-178">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-179">
              Qué vas a vender{' '}
              <span className="text-gray-500 text-2xl block mt-1" data-id="element-180">
                (aunque no sepas nada)
              </span>
            </h2>
            <p className="text-gray-400" data-id="element-181">
              Te doy 20 opciones listas (10 físicas + 10 digitales)
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-182">
            {/* Physical */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-[#0a0a0a] p-8 rounded-2xl border border-white/10 hover:border-[#25D366]/50 transition-colors" data-id="element-183">
              <div className="flex items-center gap-3 mb-6" data-id="element-184">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center" data-id="element-185">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-186">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" data-id="element-187" />
                    <circle cx="12" cy="10" r="3" data-id="element-188" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold" data-id="element-189">Físicos (Local)</h3>
              </div>
              <ul className="space-y-3" data-id="element-190">
                {['Manicura express por bloques', 'Limpieza express post-jornada', 'Compras asistidas mercado/farmacia', 'Barbería a domicilio', 'Paseo premium de perros', 'Meal prep semanal'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-191">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" data-id="element-192" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Digital */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-[#0a0a0a] p-8 rounded-2xl border border-white/10 hover:border-[#25D366]/50 transition-colors" data-id="element-193">
              <div className="flex items-center gap-3 mb-6" data-id="element-194">
                <div className="w-10 h-10 rounded-lg bg-[#25D366]/20 text-[#25D366] flex items-center justify-center" data-id="element-195">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-196">
                    <rect width="20" height="14" x="2" y="3" rx="2" data-id="element-197" />
                    <line x1="8" x2="16" y1="21" y2="21" data-id="element-198" />
                    <line x1="12" x2="12" y1="17" y2="21" data-id="element-199" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold" data-id="element-200">Digitales (WhatsApp)</h3>
              </div>
              <ul className="space-y-3" data-id="element-201">
                {['Consultoría 1 a 1 de tu oficio', 'Ebook corto de tu experiencia', 'Guía para vender por WhatsApp', 'Plantillas de mensajes', 'Plantilla de precios', 'Tabla simple de control'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-202">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#25D366]" data-id="element-203" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <p className="text-center mt-8 text-white font-medium italic" data-id="element-204">
            "No tienes que inventar nada. Solo eliges 1 y ejecutas."
          </p>
        </div>
      </section>

      {/* 5. OBJECTION/SCRIPTS SECTION */}
      <section className="py-16 px-4 bg-[#0a0a0a]" data-id="element-205">
        <div className="max-w-md mx-auto" data-id="element-206">
          <div className="text-center mb-10" data-id="element-207">
            <h2 className="text-3xl font-bold mb-4" data-id="element-208">¿Pero yo no sé vender…?</h2>
            <p className="text-gray-400" data-id="element-209">
              Perfecto. Por eso esto incluye 12 mensajes listos.
            </p>
          </div>

          <div className="bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat bg-opacity-10 p-4 rounded-2xl border border-white/10 h-[500px] overflow-y-auto custom-scrollbar relative" data-id="element-210">
            <div className="absolute inset-0 bg-[#0b141a]/90 -z-10" data-id="element-211" />

            <div className="space-y-4 py-4" data-id="element-212">
              <div className="text-center text-xs text-gray-500 my-4 bg-[#1f2c34] inline-block px-3 py-1 rounded-lg mx-auto w-fit shadow-sm" data-id="element-213">
                Hoy
              </div>

              <ChatBubble message="Hola! 👋 Vi que estás interesado en el servicio. ¿Te gustaría saber más?" isOutgoing={true} timestamp="10:00 AM" data-id="element-214" />
              <ChatBubble message="Sí, cuéntame el precio por favor." isOutgoing={false} timestamp="10:05 AM" data-id="element-215" />
              <ChatBubble message="El paquete completo cuesta $5. Incluye todo lo que necesitas para empezar hoy mismo. ¿Te paso los datos para el pago?" isOutgoing={true} timestamp="10:06 AM" data-id="element-216" />
              <ChatBubble message="Déjame pensarlo..." isOutgoing={false} timestamp="10:15 AM" data-id="element-217" />
              <ChatBubble message="Entiendo! Solo ten en cuenta que los cupos de esta semana se están llenando. Si confirmas hoy, te incluyo un bonus extra. ¿Te parece bien?" isOutgoing={true} timestamp="10:16 AM" data-id="element-218" />
              <ChatBubble message="Vale, pásame el Pago Móvil." isOutgoing={false} timestamp="10:20 AM" data-id="element-219" />
              <ChatBubble message="Excelente decisión. Aquí tienes los datos: 0414-XXX-XXXX..." isOutgoing={true} timestamp="10:21 AM" data-id="element-220" />
            </div>
          </div>

          <p className="text-center mt-6 text-sm text-gray-500 max-w-xs mx-auto" data-id="element-221">
            En WhatsApp, el error típico es dejar el chat sin siguiente paso.
            Aquí lo cambiamos por cierres directos.
          </p>
        </div>
      </section>

      {/* 6. OFFER STACK SECTION */}
      <section className="py-16 px-4 bg-[#111b21] border-y border-white/5" data-id="element-222">
        <div className="max-w-3xl mx-auto" data-id="element-223">
          <div className="bg-[#1f2c34] rounded-3xl p-8 md:p-12 shadow-2xl border border-[#25D366]/20 relative overflow-hidden" data-id="element-224">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#25D366] blur-[80px] opacity-20" data-id="element-225" />

            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10" data-id="element-226">
              Qué incluye exactamente{' '}
              <span className="text-[#25D366]" data-id="element-227">(por $5)</span>
            </h2>

            <div className="grid gap-4" data-id="element-228">
              {['20 micro-servicios (físicos y digitales)', 'Precios sugeridos en USD (para no regalarte)', 'Estructura de oferta básico / completo', '12 guiones copy/paste (apertura, oferta, cierre)', 'Cómo cobrar desde Venezuela: Pago Móvil + USDT', 'Plan de 7 días con tareas mínimas'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-center gap-4 p-4 bg-[#111b21] rounded-xl border border-white/5" data-id="element-229">
                  <div className="min-w-[24px] h-6 rounded-full bg-[#25D366] flex items-center justify-center text-black" data-id="element-230">
                    <Check className="w-4 h-4 stroke-[3]" data-id="element-231" />
                  </div>
                  <span className="font-medium text-lg text-gray-200" data-id="element-232">
                    {item}
                  </span>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 7. AUDIENCE SECTION */}
      <section className="py-16 px-4 bg-[#0a0a0a]" data-id="element-233">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-234">
          <motion.div whileHover={{
          scale: 1.02
        }} className="p-8 rounded-2xl bg-[#1a2e24] border border-[#25D366]/30" data-id="element-235">
            <h3 className="text-2xl font-bold text-[#25D366] mb-6 flex items-center gap-2" data-id="element-236">
              <Check className="w-6 h-6" data-id="element-237" /> Para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-238">
              <li className="flex items-start gap-3 text-gray-300" data-id="element-239">
                <span className="text-[#25D366]" data-id="element-240">•</span> Necesitas tu primera
                venta YA
              </li>
              <li className="flex items-start gap-3 text-gray-300" data-id="element-241">
                <span className="text-[#25D366]" data-id="element-242">•</span> Puedes escribirle a
                gente por WhatsApp
              </li>
              <li className="flex items-start gap-3 text-gray-300" data-id="element-243">
                <span className="text-[#25D366]" data-id="element-244">•</span> Quieres algo
                ejecutable sin verte "vendedor"
              </li>
            </ul>
          </motion.div>

          <motion.div whileHover={{
          scale: 1.02
        }} className="p-8 rounded-2xl bg-[#2a1a1a] border border-red-500/30" data-id="element-245">
            <h3 className="text-2xl font-bold text-red-500 mb-6 flex items-center gap-2" data-id="element-246">
              <X className="w-6 h-6" data-id="element-247" /> No es para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-248">
              <li className="flex items-start gap-3 text-gray-300" data-id="element-249">
                <span className="text-red-500" data-id="element-250">•</span> Quieres dinero sin
                hablar con nadie
              </li>
              <li className="flex items-start gap-3 text-gray-300" data-id="element-251">
                <span className="text-red-500" data-id="element-252">•</span> Quieres "automático" sin
                enviar mensajes
              </li>
              <li className="flex items-start gap-3 text-gray-300" data-id="element-253">
                <span className="text-red-500" data-id="element-254">•</span> Quieres que te motive en
                vez de ponerte a hacer
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 8. PRICING + FINAL CTA */}
      <section id="precio" className="py-20 px-4 bg-[#0a0a0a] text-center relative overflow-hidden" data-id="element-255">
        <div className="absolute inset-0 bg-gradient-to-t from-[#25D366]/10 to-transparent pointer-events-none" data-id="element-256" />

        <div className="max-w-lg mx-auto relative z-10" data-id="element-257">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} className="relative bg-[#111b21] border-2 border-[#25D366]/40 rounded-3xl p-8 md:p-10 shadow-[0_0_60px_rgba(37,211,102,0.12)] overflow-hidden" data-id="element-258">
            {/* Corner glow */}
            <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#25D366] blur-[80px] opacity-20 pointer-events-none" data-id="element-259" />
            <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-[#25D366] blur-[80px] opacity-10 pointer-events-none" data-id="element-260" />

            {/* Badge */}
            <div className="inline-block px-4 py-1.5 mb-6 bg-[#25D366]/10 border border-[#25D366]/30 rounded-full text-[#25D366] text-xs font-bold tracking-wider uppercase" data-id="element-261">
              Pago único • Entrega inmediata
            </div>

            {/* Price */}
            <div className="mb-2" data-id="element-262">
              <span className="text-7xl md:text-8xl font-black text-white tracking-tighter" data-id="element-263">
                $5
              </span>
              <span className="text-2xl md:text-3xl text-gray-500 font-bold ml-2" data-id="element-264">
                USD
              </span>
            </div>
            <p className="text-gray-400 mb-8 text-base" data-id="element-265">
              Porque el objetivo es que compres, lo apliques y veas tu primera
              venta.
            </p>

            {/* Mini feature list */}
            <div className="flex flex-col gap-2.5 mb-8 text-left max-w-xs mx-auto" data-id="element-266">
              {['20 micro-servicios listos', '12 guiones copy/paste', 'Plan de 7 días paso a paso', 'Cobro por Pago Móvil + USDT'].map((item, i) => <div key={i} className="flex items-center gap-3 text-sm text-gray-300" data-id="element-267">
                  <div className="min-w-[20px] h-5 rounded-full bg-[#25D366] flex items-center justify-center text-black" data-id="element-268">
                    <Check className="w-3 h-3 stroke-[3]" data-id="element-269" />
                  </div>
                  {item}
                </div>)}
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-white/10 mb-8" data-id="element-270" />

            {/* CTA */}
            <motion.a href="https://wa.me/13854824182?text=Quiero%20mi%20sistema!" target="_blank" rel="noopener noreferrer" onClick={() => {
            if (window.fbq) window.fbq('trackCustom', 'inicial-whatsapp');
          }} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} animate={{
            scale: [1, 1.02, 1]
          }} transition={{
            scale: {
              duration: 2,
              repeat: Infinity
            }
          }} className="relative overflow-hidden inline-flex flex-col items-center justify-center w-full bg-[#25D366] hover:bg-[#20bd5a] text-black px-8 py-5 rounded-2xl shadow-[0_0_30px_rgba(37,211,102,0.35)] transition-all group" data-id="element-271">
              <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10" data-id="element-272" />
              <div className="flex items-center gap-3 font-black text-xl md:text-2xl relative z-20" data-id="element-273">
                <MessageCircle className="w-7 h-7 fill-current" data-id="element-274" />
                QUIERO MI PRIMERA VENTA
              </div>
              <span className="text-sm font-medium mt-1 opacity-80 group-hover:opacity-100 relative z-20" data-id="element-275">
                Escribe por WhatsApp y te lo envío al instante
              </span>
            </motion.a>

            {/* Trust signals */}
            <div className="mt-6 flex items-center justify-center gap-5 text-gray-500 text-xs" data-id="element-276">
              <div className="flex items-center gap-1.5" data-id="element-277">
                <Lock className="w-3.5 h-3.5" data-id="element-278" /> Pago Seguro
              </div>
              <div className="flex items-center gap-1.5" data-id="element-279">
                <Check className="w-3.5 h-3.5" data-id="element-280" /> Entrega Inmediata
              </div>
              <div className="flex items-center gap-1.5" data-id="element-281">
                <MessageCircle className="w-3.5 h-3.5" data-id="element-282" /> Soporte WhatsApp
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-gray-600 text-sm bg-[#050505] space-y-3" data-id="element-283">
        <p data-id="element-284">
          © {new Date().getFullYear()} Sistema 7D WhatsApp-First. Hecho para
          Venezuela.
        </p>
        <div className="flex items-center justify-center gap-4" data-id="element-285">
          <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-[#25D366] transition-colors underline underline-offset-2" data-id="element-286">
            Política de Privacidad
          </button>
          <span className="text-gray-700" data-id="element-287">•</span>
          <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-[#25D366] transition-colors underline underline-offset-2" data-id="element-288">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-289">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-290">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-[#111b21] border border-white/10 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-291">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10" data-id="element-292">
                <h3 className="text-lg font-bold text-white" data-id="element-293">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-id="element-294">
                  <X className="w-4 h-4" data-id="element-295" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-5 overflow-y-auto max-h-[calc(80vh-64px)] text-sm text-gray-300 leading-relaxed space-y-4" data-id="element-296">
                {activeModal === 'privacy' ? <>
                    <p className="text-gray-400 text-xs" data-id="element-297">
                      Última actualización: Febrero 2026
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-298">
                      1. Información que recopilamos
                    </h4>
                    <p data-id="element-299">
                      Recopilamos únicamente la información que nos proporcionas
                      voluntariamente al realizar una compra, como tu nombre y
                      número de WhatsApp. No recopilamos datos de navegación,
                      cookies de seguimiento ni información de terceros.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-300">
                      2. Uso de la información
                    </h4>
                    <p data-id="element-301">Tu información se utiliza exclusivamente para:</p>
                    <ul className="list-disc pl-5 space-y-1" data-id="element-302">
                      <li data-id="element-303">Procesar y entregar tu compra por WhatsApp</li>
                      <li data-id="element-304">Brindarte soporte relacionado con el producto</li>
                      <li data-id="element-305">
                        Enviarte actualizaciones del producto que adquiriste
                      </li>
                    </ul>

                    <h4 className="text-white font-semibold text-base" data-id="element-306">
                      3. Protección de datos
                    </h4>
                    <p data-id="element-307">
                      No vendemos, compartimos ni transferimos tu información
                      personal a terceros bajo ninguna circunstancia. Tu número
                      de WhatsApp y datos de pago se manejan de forma
                      confidencial.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-308">
                      4. Almacenamiento
                    </h4>
                    <p data-id="element-309">
                      La información se almacena de forma segura y solo se
                      conserva durante el tiempo necesario para cumplir con el
                      propósito de la transacción y el soporte post-venta.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-310">
                      5. Tus derechos
                    </h4>
                    <p data-id="element-311">
                      Puedes solicitar la eliminación de tus datos en cualquier
                      momento. También puedes solicitar acceso a la información
                      que tenemos sobre ti o pedir que sea corregida.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-312">
                      6. Cambios en esta política
                    </h4>
                    <p data-id="element-313">
                      Nos reservamos el derecho de actualizar esta política en
                      cualquier momento. Los cambios serán efectivos
                      inmediatamente después de su publicación en esta página.
                    </p>
                  </> : <>
                    <p className="text-gray-400 text-xs" data-id="element-314">
                      Última actualización: Febrero 2026
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-315">
                      1. Aceptación de los términos
                    </h4>
                    <p data-id="element-316">
                      Al realizar una compra en esta página, aceptas estos
                      términos y condiciones en su totalidad. Si no estás de
                      acuerdo con alguno de estos términos, te recomendamos no
                      realizar la compra.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-317">
                      2. Descripción del producto
                    </h4>
                    <p data-id="element-318">
                      El producto es un documento digital (PDF) que contiene
                      guías, scripts y estrategias para generar ventas a través
                      de WhatsApp. Es un material educativo e informativo.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-319">
                      3. Entrega
                    </h4>
                    <p data-id="element-320">
                      El producto se entrega de forma digital e inmediata a
                      través de WhatsApp después de confirmar el pago. No se
                      realizan envíos físicos.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-321">
                      4. Política de reembolso
                    </h4>
                    <p data-id="element-322">
                      Debido a la naturaleza digital del producto y su entrega
                      inmediata, no se aceptan devoluciones ni reembolsos una
                      vez realizada la entrega del material.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-323">
                      5. Resultados
                    </h4>
                    <p data-id="element-324">
                      Los resultados pueden variar según el esfuerzo, dedicación
                      y circunstancias individuales de cada persona. No
                      garantizamos ingresos específicos ni resultados
                      financieros determinados.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-325">
                      6. Propiedad intelectual
                    </h4>
                    <p data-id="element-326">
                      Todo el contenido del producto está protegido por derechos
                      de autor. Queda prohibida su reproducción, distribución o
                      reventa sin autorización expresa.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-327">
                      7. Uso permitido
                    </h4>
                    <p data-id="element-328">
                      El producto es para uso personal únicamente. No está
                      permitido compartir, redistribuir o comercializar el
                      contenido del PDF.
                    </p>

                    <h4 className="text-white font-semibold text-base" data-id="element-329">
                      8. Modificaciones
                    </h4>
                    <p data-id="element-330">
                      Nos reservamos el derecho de modificar estos términos en
                      cualquier momento. Las modificaciones entrarán en vigor
                      desde su publicación en esta página.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <SocialProofToast data-id="element-331" />
      <LiveViewerFloat data-id="element-332" />
    </div>;
}